import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { createThumbnailUrl, applyImageAdjustments } from '../utils/imageUtils';

const PhotoEditor = ({ photo, photos, onClose, onSave, onNavigate }) => {
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    sharpen: 0,
  });
  const [saving, setSaving] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showEnhance, setShowEnhance] = useState(false);

  useEffect(() => {
    if (photo && photos) {
      const index = photos.findIndex(p => p.id === photo.id);
      setCurrentIndex(index);
    }
  }, [photo, photos]);

  const currentPhoto = photos[currentIndex] || photo;
  const imageUrl = createThumbnailUrl(currentPhoto);

  const handleAdjustment = (key, value) => {
    setAdjustments(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Apply the adjustments and create a new image
      const imageUrl = createThumbnailUrl(currentPhoto);
      const adjustedImageBlob = await applyImageAdjustments(imageUrl, adjustments);
      
      // Upload the edited blob to replace the original
      await onSave(currentPhoto, adjustments, adjustedImageBlob);
      
      alert('✅ Edits saved and applied successfully!');
      onClose();
      
      // Refresh the page to show updated image
      if (window.refreshPhotos) {
        window.refreshPhotos();
      }
    } catch (error) {
      console.error('Error saving edits:', error);
      alert('Failed to save edits. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      if (onNavigate) onNavigate(photos[newIndex], newIndex);
    }
  };

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      if (onNavigate) onNavigate(photos[newIndex], newIndex);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrevious,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const filterStyle = {
    filter: `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm">
        <h3 className="text-white font-semibold truncate flex-1">
          {currentPhoto.fileName}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              if (window.confirm('Delete this photo?')) {
                // Call parent delete handler
                if (window.handlePhotoDelete) {
                  const success = await window.handlePhotoDelete(currentPhoto);
                  if (success) {
                    // Editor will auto-close from parent
                  }
                }
              }
            }}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
            title="Delete Photo"
          >
            🗑️ Delete
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-semibold"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Main Image with Swipe */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden" {...swipeHandlers}>
        {/* Previous Button */}
        {currentIndex > 0 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          >
            ‹
          </button>
        )}

        {/* Image */}
        <img
          src={imageUrl}
          alt={currentPhoto.fileName}
          style={filterStyle}
          className="max-w-full max-h-full object-contain"
        />

        {/* Next Button */}
        {currentIndex < photos.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          >
            ›
          </button>
        )}

        {/* Photo Counter */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full backdrop-blur-sm">
          {currentIndex + 1} / {photos.length}
        </div>
      </div>

      {/* Controls Panel */}
      <div className="bg-gray-900 p-4">
        {/* Toggle Button */}
        <button
          onClick={() => setShowEnhance(!showEnhance)}
          className="w-full mb-4 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-semibold flex items-center justify-center gap-2"
        >
          {showEnhance ? '▼ Hide Enhance' : '▲ Show Enhance'}
        </button>

        {showEnhance && (
          <div className="space-y-4 max-h-64 overflow-y-auto scrollbar-thin">
        {/* Brightness */}
        <div>
          <label className="text-white text-sm mb-2 flex justify-between">
            <span>☀️ Brightness</span>
            <span>{adjustments.brightness}%</span>
          </label>
          <input
            type="range"
            min="50"
            max="150"
            value={adjustments.brightness}
            onChange={(e) => handleAdjustment('brightness', e.target.value)}
            className="w-full accent-primary-500"
          />
        </div>

        {/* Contrast */}
        <div>
          <label className="text-white text-sm mb-2 flex justify-between">
            <span>🔆 Contrast</span>
            <span>{adjustments.contrast}%</span>
          </label>
          <input
            type="range"
            min="50"
            max="150"
            value={adjustments.contrast}
            onChange={(e) => handleAdjustment('contrast', e.target.value)}
            className="w-full accent-primary-500"
          />
        </div>

        {/* Saturation */}
        <div>
          <label className="text-white text-sm mb-2 flex justify-between">
            <span>🎨 Saturation</span>
            <span>{adjustments.saturation}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="200"
            value={adjustments.saturation}
            onChange={(e) => handleAdjustment('saturation', e.target.value)}
            className="w-full accent-primary-500"
          />
        </div>

        {/* Sharpen */}
        <div>
          <label className="text-white text-sm mb-2 flex justify-between">
            <span>✨ Sharpen</span>
            <span>{adjustments.sharpen}</span>
          </label>
          <input
            type="range"
            min="0"
            max="10"
            value={adjustments.sharpen}
            onChange={(e) => handleAdjustment('sharpen', e.target.value)}
            className="w-full accent-primary-500"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => setAdjustments({ brightness: 100, contrast: 100, saturation: 100, sharpen: 0 })}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoEditor;

